import { type PropsWithChildren, createContext, useContext } from 'react';
import { jwtDecode, JwtPayload as BaseJwtPayload } from 'jwt-decode';
import { isEmpty, isNil } from 'lodash-es';

export const AUTH_JWT_KEY = 'app.auth_jwt';
const MINUTE = 60000;

interface JwtPayload extends BaseJwtPayload {
  admin: boolean;
  exp: number;
}

interface Props {
  jwt: JwtPayload | null | undefined;
}

const Context = createContext<Props>({ jwt: undefined });

export const JwtProvider = ({ children }: PropsWithChildren) => {
  const jwt = localStorage.getItem(AUTH_JWT_KEY);
  let decoded: JwtPayload | undefined;
  if (jwt) {
    decoded = jwtDecode<JwtPayload>(jwt);
  }
  return <Context.Provider value={{ jwt: decoded }}>{children}</Context.Provider>;
};

const isValid = (jwt: JwtPayload | null | undefined): boolean => {
  if (!isNil(jwt) && !isEmpty(jwt)) {
    const expMillis = jwt.exp * 1000;
    const diff = expMillis - new Date().getTime() - MINUTE * 2;
    return diff > 0;
  }

  return false;
};

export const useJwt = () => {
  const data = useContext(Context);
  return {
    isValid: isValid(data.jwt),
  };
};

export const useUserId = () => {
  const data = useContext(Context);
  if (!data.jwt?.sub) {
    throw new Error('useUserId must be used within a UserIdProvider, if userId is optional use useUserIdMaybe');
  }
  return data.jwt.sub;
};

export const useUserIdMaybe = () => {
  const data = useContext(Context);
  return data.jwt?.sub;
};
